// Legal.tsx - les quatre pages juridiques : Mentions légales, Politique de
// confidentialité, CGU (usage de l'app, grand public + restaurateurs) et CGV
// (abonnement restaurateur).
//
// ⚠️ Le contenu est aligné sur ce que le produit fait RÉELLEMENT, tel que
// documenté dans `docs/rules/` - notamment :
//   · purge-minuit.md ................ purge quotidienne des demandes du jour
//   · historique-reservations.md ..... historique nominal 3 ans + effacement
//   · clients-agreges.md ............. agrégats anonymes, clé peppérée
//   · demandes-anonymisation.md ...... téléphone masqué avant validation
//   · notifications-resto.md ......... jetons des appareils restaurateurs
//   · modification-resa-client.md .... le client modifie/annule lui-même
// Toute évolution de ces règles doit être répercutée ICI dans le même tour de
// travail : une politique de confidentialité qui ment est pire qu'absente.
//
// Les champs [entre crochets] (constants.LEGAL) sont à compléter - surlignés
// à l'écran tant qu'ils restent des placeholders.
import type { ReactElement, ReactNode } from "react";
import { Icon } from "@hop-resto/ui";
import { LEGAL, isPlaceholder } from "../lib/constants";
import { clearConsent } from "../lib/consent";
import type { Path } from "../lib/router";

const UPDATED = "19 août 2026";

function LegalShell({
  title,
  navigate,
  children,
}: {
  title: string;
  navigate: (to: Path) => void;
  children: ReactNode;
}): ReactElement {
  return (
    <main className="legal container">
      <button type="button" className="back-link" onClick={() => navigate("/")}>
        <Icon name="chevL" size={16} stroke={2.4} /> Retour
      </button>
      <h1>{title}</h1>
      <p className="legal-updated">Dernière mise à jour : {UPDATED}</p>
      {children}
    </main>
  );
}

/** Valeur légale ; surlignée tant qu'elle n'est pas complétée. */
function L({ v }: { v: string }): ReactElement {
  return isPlaceholder(v) ? <span className="todo">{v}</span> : <span>{v}</span>;
}

/** Lien interne vers une autre page légale. */
function Lien({
  to,
  navigate,
  children,
}: {
  to: Path;
  navigate: (to: Path) => void;
  children: ReactNode;
}): ReactElement {
  return (
    <a
      href={to}
      onClick={(e) => {
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

// ═══════════════════════════════════════════════════════════════
// Mentions légales
// ═══════════════════════════════════════════════════════════════

export function MentionsLegales({ navigate }: { navigate: (to: Path) => void }): ReactElement {
  return (
    <LegalShell title="Mentions légales" navigate={navigate}>
      <h2>Éditeur du site</h2>
      <p>
        Le site <strong>Hop Resto</strong> est édité par <L v={LEGAL.editeur} />,{" "}
        <L v={LEGAL.statut} />, dont l'adresse est <L v={LEGAL.adresse} />.
        <br />
        Contact : <L v={LEGAL.email} />
        <br />
        Directeur de la publication : <L v={LEGAL.directeur} />
      </p>

      <h2>Hébergement</h2>
      <p>
        Le site et le service sont hébergés par <strong>Firebase</strong> (Hosting,
        Firestore, Cloud Functions), un service fourni par Google Ireland Limited,
        Gordon House, Barrow Street, Dublin 4, Irlande -{" "}
        <a href="https://firebase.google.com" target="_blank" rel="noreferrer">
          firebase.google.com
        </a>
        . Les données sont hébergées dans l'Union européenne.
      </p>

      <h2>Propriété intellectuelle</h2>
      <p>
        La marque « Hop Resto », le logo, les textes, les visuels et le logiciel de ce
        site sont la propriété de leur éditeur. Toute reproduction non autorisée est
        interdite. Les noms, logos et photographies des restaurants affichés dans le
        service restent la propriété de leurs titulaires respectifs, qui en autorisent
        l'usage aux seules fins du service.
      </p>

      <h2>Documents contractuels</h2>
      <p>
        L'utilisation du service est régie par les{" "}
        <Lien to="/cgu" navigate={navigate}>
          conditions générales d'utilisation
        </Lien>
        . L'abonnement des restaurateurs est régi par les{" "}
        <Lien to="/cgv" navigate={navigate}>
          conditions générales de vente
        </Lien>
        . Le traitement des données personnelles est décrit dans la{" "}
        <Lien to="/confidentialite" navigate={navigate}>
          politique de confidentialité
        </Lien>
        .
      </p>

      <h2>Nous contacter</h2>
      <p>
        Pour toute question relative au site ou au service : <L v={LEGAL.email} /> ou{" "}
        <L v={LEGAL.telephone} />.
      </p>
    </LegalShell>
  );
}

// ═══════════════════════════════════════════════════════════════
// Politique de confidentialité
// ═══════════════════════════════════════════════════════════════

export function Confidentialite({ navigate }: { navigate: (to: Path) => void }): ReactElement {
  const retirerConsentement = () => {
    clearConsent();
    window.location.reload();
  };

  return (
    <LegalShell title="Politique de confidentialité" navigate={navigate}>
      <p>
        Hop Resto est conçu selon un principe simple : <strong>collecter le moins de
        données possible, et les garder le moins longtemps possible</strong>. Chercher
        une table ne demande ni compte, ni email, ni nom.
      </p>

      <h2>En bref</h2>
      <ul>
        <li>
          <strong>Aucun compte</strong> n'est nécessaire pour consulter les tables
          disponibles.
        </li>
        <li>
          Vos coordonnées ne sont transmises <strong>qu'au restaurant que vous avez
          choisi</strong>, jamais aux autres.
        </li>
        <li>
          Tout ce qui concerne la journée est <strong>effacé chaque nuit</strong>.
        </li>
        <li>
          Aucune donnée personnelle n'est <strong>vendue ni cédée</strong> à des tiers.
        </li>
      </ul>

      <h2>1. Chercher une table : aucune donnée personnelle</h2>
      <ul>
        <li>
          <strong>Aucun compte</strong> n'est requis - ni nom, ni email, ni numéro de
          téléphone pour consulter les tables disponibles.
        </li>
        <li>
          <strong>Géolocalisation</strong> : traitée uniquement sur votre appareil pour
          centrer la carte et trier les restaurants. Elle n'est{" "}
          <strong>jamais envoyée à nos serveurs</strong>.
        </li>
        <li>
          <strong>Notifications</strong> : si vous activez le mode recherche, un
          identifiant technique pseudonyme (jeton de notification) est utilisé dans le
          seul but de vous prévenir des tables disponibles. L'activation est explicite,
          se coupe automatiquement à 23 h et peut être désactivée à tout moment.
        </li>
      </ul>

      <h2>2. Demander une table</h2>
      <p>
        Pour transmettre votre demande à un restaurant, l'application collecte votre{" "}
        <strong>prénom</strong>, votre <strong>numéro de téléphone</strong>, le nombre de
        convives, l'heure souhaitée, un éventuel <strong>commentaire</strong> (allergie,
        poussette…) et un identifiant technique de notification. Ces informations servent
        à la mise en relation : le restaurateur vous rappelle si besoin, et une
        notification vous informe de l'issue.
      </p>
      <ul>
        <li>
          <strong>Votre téléphone reste masqué</strong> au restaurateur tant qu'il n'a
          pas validé votre demande : avant l'arbitrage, il ne voit que votre prénom et le
          nombre de convives.
        </li>
        <li>
          <strong>Un seul restaurant</strong> reçoit vos coordonnées : celui dont la
          table a été confirmée. Les demandes non retenues sont effacées.
        </li>
        <li>
          <strong>Demandes du jour, jeton de notification et commentaire</strong> :
          effacés automatiquement <strong>chaque nuit</strong>.
        </li>
        <li>
          Vous pouvez <strong>annuler</strong> votre réservation à tout moment depuis
          l'application, et <strong>modifier</strong> l'heure ou le nombre de convives
          d'une réservation à venir.
        </li>
      </ul>

      <h2>3. Historique des réservations confirmées (3 ans)</h2>
      <p>
        Lorsqu'une réservation est <strong>confirmée</strong>, le restaurant en conserve
        une trace nominative - prénom, téléphone, date, heure, nombre de couverts, issue
        (venue, annulée, non honorée) - pendant{" "}
        <strong>{LEGAL.dureeHistorique}</strong>. C'est ce qui lui permet de reconnaître
        ses habitués et de vous joindre en cas de besoin. Cette conservation repose sur
        l'intérêt légitime du restaurateur à tenir son registre de réservations.
      </p>
      <ul>
        <li>
          Au terme des {LEGAL.dureeHistorique}, vos <strong>coordonnées sont effacées
          automatiquement</strong> : seule subsiste une ligne anonyme (date, couverts,
          issue) qui ne permet plus de vous identifier.
        </li>
        <li>
          Vous pouvez demander cet effacement <strong>à tout moment</strong>, sans
          attendre : auprès du restaurant, ou en nous écrivant à <L v={LEGAL.email} />.
        </li>
        <li>
          Le commentaire que vous avez laissé et les notes internes du restaurateur{" "}
          <strong>n'entrent jamais</strong> dans cet historique.
        </li>
      </ul>

      <h2>4. Statistiques anonymes</h2>
      <p>
        Pour mesurer l'activité du service et fournir aux restaurants leurs indicateurs
        (remplissage, heures de pointe, taux de présence), nous produisons des{" "}
        <strong>statistiques agrégées</strong>. Elles ne contiennent{" "}
        <strong>ni nom, ni téléphone</strong> : seulement des compteurs.
      </p>
      <p>
        Nous mesurons également, à l'échelle du réseau, des indicateurs de fréquentation
        anonymes (nombre de clients distincts, part de réservations passées par
        l'application). Pour cela, votre numéro de téléphone est transformé par une
        fonction cryptographique irréversible, avec une clé secrète :{" "}
        <strong>le numéro lui-même n'est jamais stocké dans ces statistiques</strong>, et
        l'empreinte obtenue ne permet à personne de le retrouver. Aucune information
        nominative n'apparaît dans ces tableaux, qui ne sont consultables que par
        l'éditeur. Ces empreintes sont supprimées{" "}
        <strong>{LEGAL.dureeHistorique} après votre dernière réservation</strong>.
      </p>

      <h2>5. Restaurateurs</h2>
      <ul>
        <li>
          <strong>Formulaire de contact</strong> : prénom, nom, nom du restaurant, ville,
          email et/ou téléphone. Ces données servent uniquement à vous rappeler pour
          présenter le service (mesures précontractuelles, art. 6.1.b RGPD). Conservation :{" "}
          {LEGAL.dureeConservationLeads} maximum.
        </li>
        <li>
          <strong>Compte restaurateur</strong> : une adresse email et un mot de passe,
          gérés par Firebase Authentication. Ils servent uniquement à vous connecter à
          votre espace.
        </li>
        <li>
          <strong>Appareils notifiés</strong> : lorsque vous activez les notifications
          sur un téléphone, une tablette ou un ordinateur, un jeton technique de
          notification est enregistré pour cet appareil. Il ne contient aucune donnée
          personnelle, n'est lisible que par vous, et disparaît dès que vous désactivez
          les notifications sur l'appareil concerné.
        </li>
      </ul>

      <h2>6. Mesure d'audience</h2>
      <p>
        Ce site utilise Google Analytics <strong>uniquement si vous l'acceptez</strong> via
        le bandeau de consentement. Tant que vous n'avez pas accepté, aucun cookie de
        mesure d'audience n'est déposé. Vous pouvez changer d'avis à tout moment :
      </p>
      <p>
        <button type="button" className="btn btn-ghost btn-sm" onClick={retirerConsentement}>
          Modifier mon choix cookies
        </button>
      </p>

      <h2>7. Sécurité anti-abus</h2>
      <p>
        Pour protéger le formulaire de contact et les demandes de réservation contre les
        envois automatisés, le service peut recourir à un mécanisme anti-abus (Firebase
        App Check / reCAPTCHA de Google). Il ne sert qu'à distinguer un humain d'un robot.
      </p>

      <h2>8. Qui fait quoi</h2>
      <p>
        Pour les données de réservation, le <strong>restaurant</strong> que vous avez
        choisi est responsable de traitement ; <L v={LEGAL.editeur} /> agit comme{" "}
        <strong>sous-traitant</strong> et lui fournit l'outil. Pour le site, le formulaire
        de contact et les statistiques anonymes du réseau,{" "}
        <L v={LEGAL.editeur} /> est responsable de traitement. Nos hébergeurs et
        sous-traitants techniques (Google Firebase) n'utilisent ces données que pour
        exécuter le service.
      </p>

      <h2>9. Vos droits</h2>
      <p>
        Conformément au RGPD, vous disposez d'un droit d'accès, de rectification,
        d'effacement, d'opposition et de portabilité des données vous concernant.
        Écrivez-nous : <L v={LEGAL.email} /> - nous répondons sous un mois. Vous pouvez
        également saisir la CNIL (
        <a href="https://www.cnil.fr" target="_blank" rel="noreferrer">
          cnil.fr
        </a>
        ).
      </p>
    </LegalShell>
  );
}

// ═══════════════════════════════════════════════════════════════
// CGU - conditions générales d'utilisation
// ═══════════════════════════════════════════════════════════════

export function CGU({ navigate }: { navigate: (to: Path) => void }): ReactElement {
  return (
    <LegalShell title="Conditions générales d'utilisation" navigate={navigate}>
      <p>
        Les présentes conditions régissent l'utilisation du service{" "}
        <strong>Hop Resto</strong> - le site, l'application mobile destinée au grand
        public et l'application de gestion destinée aux restaurateurs. Utiliser le
        service vaut acceptation de ces conditions.
      </p>

      <h2>1. Ce qu'est Hop Resto</h2>
      <p>
        Hop Resto est un service de <strong>mise en relation</strong> entre des
        restaurants disposant de tables libres et des personnes cherchant où manger. Nous
        ne sommes ni restaurateur, ni intermédiaire de paiement :{" "}
        <strong>le contrat de restauration se noue directement entre vous et le
        restaurant</strong>. Les prix, la carte, le service et la qualité des repas
        relèvent de la seule responsabilité du restaurant.
      </p>

      <h2>2. Accès au service</h2>
      <ul>
        <li>
          Le service est <strong>gratuit</strong> pour le grand public et ne nécessite
          pas de création de compte.
        </li>
        <li>
          Il est accessible aux personnes majeures ou mineures disposant de
          l'autorisation de leur représentant légal.
        </li>
        <li>
          L'accès à l'espace restaurateur est réservé aux professionnels titulaires d'un
          compte, dans les conditions prévues aux{" "}
          <Lien to="/cgv" navigate={navigate}>
            CGV
          </Lien>
          .
        </li>
      </ul>

      <h2>3. Réserver une table</h2>
      <ul>
        <li>
          Une demande de réservation n'est <strong>pas</strong> une réservation ferme :
          elle ne devient effective qu'après <strong>confirmation du restaurant</strong>,
          qui vous est notifiée.
        </li>
        <li>
          Une table affichée peut disparaître à tout moment - elle est disponible{" "}
          <strong>jusqu'à ce qu'un autre client l'obtienne</strong>. Vous en êtes alors
          informé.
        </li>
        <li>
          Vous pouvez <strong>annuler</strong> votre réservation à tout moment depuis
          l'application, et <strong>modifier</strong> l'heure ou le nombre de convives
          d'une réservation à venir, dans la limite des places disponibles.
        </li>
        <li>
          Vous vous engagez à fournir des informations <strong>exactes</strong> (prénom,
          téléphone, nombre de convives) et à <strong>prévenir le restaurant</strong> si
          vous ne pouvez pas honorer une réservation.
        </li>
      </ul>

      <h2>4. Ce que nous attendons de vous</h2>
      <p>Vous vous engagez à ne pas :</p>
      <ul>
        <li>
          effectuer de réservations fictives, en série, ou pour le compte de tiers sans
          leur accord ;
        </li>
        <li>
          utiliser de moyen automatisé (robot, script) pour accéder au service ou en
          extraire le contenu ;
        </li>
        <li>
          perturber le fonctionnement du service, tenter d'y accéder sans autorisation,
          ou porter atteinte aux droits d'autrui.
        </li>
      </ul>
      <p>
        En cas de manquement, nous pouvons restreindre ou suspendre l'accès au service,
        après avertissement lorsque les circonstances le permettent.
      </p>

      <h2>5. Ce que le restaurateur s'engage à faire</h2>
      <ul>
        <li>
          <strong>Honorer</strong> les réservations qu'il a confirmées, et prévenir le
          client sans délai s'il ne le peut pas ;
        </li>
        <li>
          ne publier que des <strong>tables réellement disponibles</strong>, et retirer
          sans tarder celles qui ne le sont plus ;
        </li>
        <li>
          tenir <strong>à jour</strong> ses horaires, ses fermetures et sa capacité ;
        </li>
        <li>
          utiliser les coordonnées des clients <strong>uniquement</strong> pour la
          réservation concernée, et respecter la réglementation sur les données
          personnelles.
        </li>
      </ul>

      <h2>6. Disponibilité du service</h2>
      <p>
        Nous nous efforçons d'assurer un service continu, sans pouvoir le garantir : des
        interruptions peuvent survenir pour maintenance, mise à jour, ou du fait de nos
        hébergeurs et des réseaux de télécommunication. Le service dépend également des
        notifications des systèmes Android et iOS, sur lesquelles nous n'avons pas la
        main : leur acheminement ne peut être garanti.
      </p>

      <h2>7. Responsabilité</h2>
      <p>
        Notre responsabilité se limite au <strong>fonctionnement de la mise en
        relation</strong>. Nous ne saurions être tenus responsables du déroulement du
        repas, d'un différend avec un restaurant, d'une réservation non honorée par l'une
        ou l'autre partie, ni des conséquences d'une notification non reçue du fait du
        terminal ou du réseau de l'utilisateur.
      </p>

      <h2>8. Données personnelles</h2>
      <p>
        Le traitement de vos données est décrit dans notre{" "}
        <Lien to="/confidentialite" navigate={navigate}>
          politique de confidentialité
        </Lien>
        , qui fait partie intégrante des présentes conditions.
      </p>

      <h2>9. Évolution des conditions</h2>
      <p>
        Ces conditions peuvent être modifiées pour suivre les évolutions du service ou de
        la réglementation. La version applicable est celle publiée sur cette page, dont
        la date de mise à jour figure en tête.
      </p>

      <h2>10. Droit applicable</h2>
      <p>
        Les présentes conditions sont soumises au <strong>droit français</strong>. En cas
        de différend, une solution amiable sera recherchée en priorité : écrivez-nous à{" "}
        <L v={LEGAL.email} />. À défaut, les tribunaux français sont compétents. Le
        consommateur peut recourir gratuitement à un médiateur de la consommation ou à la
        plateforme européenne de règlement en ligne des litiges.
      </p>
    </LegalShell>
  );
}

// ═══════════════════════════════════════════════════════════════
// CGV - conditions générales de vente (restaurateurs)
// ═══════════════════════════════════════════════════════════════

export function CGV({ navigate }: { navigate: (to: Path) => void }): ReactElement {
  return (
    <LegalShell title="Conditions générales de vente" navigate={navigate}>
      <p>
        Les présentes conditions régissent l'abonnement au service{" "}
        <strong>Hop Resto</strong> souscrit par un <strong>professionnel de la
        restauration</strong>. Elles complètent les{" "}
        <Lien to="/cgu" navigate={navigate}>
          conditions générales d'utilisation
        </Lien>
        .
      </p>

      <h2>1. L'offre</h2>
      <p>
        Hop Resto est proposé en <strong>une seule offre, complète</strong> : il n'existe
        ni version bridée, ni module payant en supplément. Elle comprend l'accès à
        l'application de gestion, la publication de tables de dernière minute, les
        réservations à l'avance, le plan de salle, les statistiques, les notifications,
        les mises à jour et l'assistance.
      </p>
      <p>
        Le détail des possibilités incluses figure sur la{" "}
        <Lien to="/" navigate={navigate}>
          page d'accueil
        </Lien>
        , section « Notre offre ».
      </p>

      <h2>2. Souscription</h2>
      <p>
        La souscription intervient après un échange avec notre équipe, sur devis ou bon
        de commande accepté. La création du compte et de la fiche restaurant vaut mise en
        service. <L v={LEGAL.editeur} /> se réserve le droit de refuser une souscription
        pour un motif légitime.
      </p>

      <h2>3. Prix et facturation</h2>
      <ul>
        <li>
          Le prix de l'abonnement est celui indiqué au devis accepté ; il est exprimé{" "}
          <strong>hors taxes</strong>, la TVA au taux en vigueur s'y ajoutant.
        </li>
        <li>
          La facturation est <strong>{LEGAL.periodiciteFacturation}</strong>, à terme à
          échoir, par prélèvement ou virement.
        </li>
        <li>
          Les tarifs peuvent évoluer ; toute évolution est notifiée{" "}
          <strong>{LEGAL.preavisTarifaire}</strong> à l'avance et n'entre en vigueur qu'à
          l'échéance suivante. Vous pouvez alors résilier sans frais.
        </li>
      </ul>

      <h2>4. Retard de paiement</h2>
      <p>
        Conformément aux articles L.441-10 et D.441-5 du code de commerce, tout retard de
        paiement entraîne de plein droit des pénalités au taux d'intérêt légal majoré,
        ainsi qu'une indemnité forfaitaire de recouvrement de <strong>40 €</strong>. En
        cas d'impayé persistant, l'accès au service peut être suspendu après mise en
        demeure restée sans effet.
      </p>

      <h2>5. Durée, résiliation</h2>
      <ul>
        <li>
          L'abonnement est conclu <strong>sans engagement de durée</strong>, reconduit
          tacitement à chaque échéance.
        </li>
        <li>
          Vous pouvez résilier à tout moment, avec un préavis de{" "}
          <strong>{LEGAL.preavisResiliation}</strong>, par simple écrit à{" "}
          <L v={LEGAL.email} />. La résiliation prend effet à la fin de la période en
          cours ; les sommes déjà réglées pour cette période restent dues.
        </li>
        <li>
          Nous pouvons résilier dans les mêmes conditions, ou immédiatement en cas de
          manquement grave (impayé, usage frauduleux, atteinte à la sécurité du service).
        </li>
      </ul>

      <h2>6. Droit de rétractation</h2>
      <p>
        L'abonnement étant souscrit par un professionnel pour les besoins de son
        activité, le droit de rétractation de l'article L.221-18 du code de la
        consommation <strong>ne s'applique pas</strong>, sauf lorsque les conditions de
        l'article L.221-3 sont réunies (professionnel employant moins de six salariés et
        objet du contrat hors de son activité principale).
      </p>

      <h2>7. Mise en service et assistance</h2>
      <p>
        La création de votre fiche et le paramétrage initial sont{" "}
        <strong>compris</strong>. L'assistance est assurée par email et par téléphone aux
        coordonnées indiquées dans l'application (Paramètres → Aide), aux jours et heures
        ouvrés.
      </p>

      <h2>8. Disponibilité et évolutions</h2>
      <p>
        Le service est fourni en l'état, dans une <strong>obligation de moyens</strong>.
        Nous ne garantissons ni un taux de disponibilité contractuel, ni un volume de
        réservations : Hop Resto met en relation, il ne promet pas de clientèle. Le
        service évolue régulièrement ; les mises à jour sont automatiques et comprises
        dans l'abonnement. Une fonctionnalité peut être modifiée ou retirée, sans
        diminution substantielle de la valeur de l'offre.
      </p>

      <h2>9. Vos données, votre bien</h2>
      <ul>
        <li>
          Les données de votre restaurant et de vos clients{" "}
          <strong>vous appartiennent</strong>. Nous les traitons en qualité de
          sous-traitant, pour votre compte et selon vos instructions.
        </li>
        <li>
          Vous pouvez en obtenir <strong>l'export</strong> à tout moment pendant
          l'abonnement, et jusqu'à <strong>{LEGAL.dureeReversibilite}</strong> après sa
          fin (réversibilité).
        </li>
        <li>
          Passé ce délai, elles sont <strong>supprimées</strong> de nos serveurs, sous
          réserve des durées légales de conservation.
        </li>
      </ul>

      <h2>10. Responsabilité</h2>
      <p>
        Notre responsabilité, toutes causes confondues, est limitée au montant des sommes
        versées au titre de l'abonnement au cours des{" "}
        <strong>douze mois</strong> précédant le fait générateur. Sont exclus les
        préjudices indirects : perte de chiffre d'affaires, de clientèle ou d'image.
      </p>

      <h2>11. Confidentialité</h2>
      <p>
        Chaque partie s'engage à préserver la confidentialité des informations non
        publiques échangées. Nous nous réservons le droit de citer votre enseigne comme
        référence commerciale ; vous pouvez vous y opposer à tout moment par simple
        écrit.
      </p>

      <h2>12. Droit applicable</h2>
      <p>
        Les présentes conditions sont soumises au <strong>droit français</strong>. En cas
        de différend, une solution amiable sera recherchée en priorité. À défaut,
        compétence est attribuée aux tribunaux du ressort du siège de{" "}
        <L v={LEGAL.editeur} />.
      </p>
    </LegalShell>
  );
}
